// src/admin/media/MediaDrawer.jsx

import React, { useState, useEffect } from 'react';
import { supabase } from 'src/lib/supabase';

const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
};

const MediaDrawer = ({ media, onClose, onUpdate, showToast }) => {
  const type = media.metadata?.type || 'image';
  const bucket = media.metadata?.bucket;
  const path = media.metadata?.path;

  const [altText, setAltText] = useState(media.metadata?.alt_text || '');
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [replacing, setReplacing] = useState(false);

  useEffect(() => {
    loadLinks();
  }, [media.id]);

  const loadLinks = async () => {
    setLoadingLinks(true);
    try {
      const { data, error } = await supabase
        .from('media_links')
        .select('*')
        .eq('media_id', media.id);
      
      if (!error && data) setLinks(data);
    } catch (error) {
      console.error('Error loading links:', error);
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleSaveAltText = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('media')
        .update({ metadata: { ...media.metadata, alt_text: altText } })
        .eq('id', media.id);
      
      if (error) throw error;
      
      if (onUpdate) await onUpdate();
      if (showToast) showToast('Alt text updated successfully', 'success');
    } catch (error) {
      console.error('Error updating alt text:', error);
      if (showToast) showToast('Failed to update alt text', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this media permanently? This action cannot be undone.')) return;
    
    try {
      // Delete from storage
      if (path && bucket) {
        const { error: storageError } = await supabase.storage.from(bucket).remove([path]);
        if (storageError) console.error('Storage delete error:', storageError);
      }
      
      // Delete from database
      const { error } = await supabase.from('media').delete().eq('id', media.id);
      if (error) throw error;
      
      if (showToast) showToast('Media deleted successfully', 'success');
      if (onUpdate) await onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting media:', error);
      if (showToast) showToast('Failed to delete media', 'error');
    }
  };

  const handleReplace = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const isImage = file.type.startsWith('image/');
    const isValidType = isImage || file.type.startsWith('video/');
    
    if (!isValidType) {
      if (showToast) showToast('Invalid file type. Please upload an image or video.', 'error');
      return;
    }
    
    if (window.confirm('Replace this file? The old version will be permanently deleted.')) {
      setReplacing(true);
      try {
        const replaceBucket = bucket || 'content';

        // Delete old file from storage
        if (path && bucket) {
          await supabase.storage.from(bucket).remove([path]);
        }
        
        // Upload new file
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${replaceBucket}/${media.id}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from(replaceBucket)
          .upload(filePath, file);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage.from(replaceBucket).getPublicUrl(filePath);
        
        // Update media record — real jsonb shape, not flat columns
        const { error: updateError } = await supabase
          .from('media')
          .update({
            original_name: file.name,
            file_size: file.size,
            mime_type: file.type,
            urls: { original: publicUrl },
            metadata: { ...media.metadata, bucket: replaceBucket, path: filePath, type: isImage ? 'image' : 'video' },
          })
          .eq('id', media.id);
        
        if (updateError) throw updateError;
        
        if (showToast) showToast('File replaced successfully', 'success');
        if (onUpdate) await onUpdate();
        onClose();
      } catch (error) {
        console.error('Error replacing file:', error);
        if (showToast) showToast('Failed to replace file', 'error');
      } finally {
        setReplacing(false);
      }
    }
    event.target.value = '';
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    if (showToast) showToast(`${label} copied to clipboard!`, 'success');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: 480,
      height: '100vh',
      backgroundColor: 'white',
      boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Plus Jakarta Sans', sans-serif"
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #F1F5F9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'white'
      }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F172A' }}>
          Media Details
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 28,
            cursor: 'pointer',
            color: '#64748B',
            padding: 0,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ×
        </button>
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {/* Preview */}
        <div style={{
          backgroundColor: '#F8FAFC',
          borderRadius: 12,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 280,
          border: '1px solid #F1F5F9'
        }}>
          {type === 'image' ? (
            <img
              src={media.urls?.original}
              alt={altText || media.original_name}
              style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8 }}
            />
          ) : (
            <video controls style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 8 }}>
              <source src={media.urls?.original} />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
        
        {/* Details */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0F172A' }}>Details</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#64748B' }}>Type:</span>
              <span style={{ fontWeight: 500 }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  backgroundColor: type === 'image' ? '#ECFDF5' : '#FEF3C7',
                  color: type === 'image' ? '#059669' : '#D97706',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600
                }}>
                  {type.toUpperCase()}
                </span>
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B' }}>Bucket:</span>
              <span style={{ fontWeight: 500 }}>{bucket || 'Unknown'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B' }}>Original filename:</span>
              <span>{media.original_name || 'Unknown'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B' }}>Size:</span>
              <span>{formatFileSize(media.file_size)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B' }}>Uploaded:</span>
              <span>{formatRelativeTime(media.created_at)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B' }}>Media ID:</span>
              <span style={{ fontSize: 11, fontFamily: 'monospace' }}>#{media.id}</span>
            </div>
          </div>
        </div>
        
        {/* Alt Text Editor */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0F172A' }}>Alt Text (SEO)</h4>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Describe this image for accessibility and SEO"
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                fontSize: 13,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                outline: 'none'
              }}
            />
            <button
              onClick={handleSaveAltText}
              disabled={saving}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6366F1',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        
        {/* URLs */}
        {media.urls && Object.keys(media.urls).length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0F172A' }}>File URLs</h4>
            {Object.entries(media.urls).map(([label, url]) => (
              <div key={label} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid #F1F5F9'
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{label}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#64748B', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>
                    {url.slice(-40)}
                  </span>
                  <button
                    onClick={() => copyToClipboard(url, `${label} URL`)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: 6,
                      fontSize: 11,
                      cursor: 'pointer',
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Usage */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0F172A' }}>Used In</h4>
          {loadingLinks ? (
            <div style={{ fontSize: 13, color: '#64748B', padding: '12px', backgroundColor: '#F8FAFC', borderRadius: 8 }}>Loading links...</div>
          ) : links.length === 0 ? (
            <div style={{ fontSize: 13, color: '#64748B', padding: '12px', backgroundColor: '#F8FAFC', borderRadius: 8, textAlign: 'center' }}>
              Not linked to any content
            </div>
          ) : (
            links.map(link => (
              <div key={link.id} style={{
                padding: '10px 0',
                borderBottom: '1px solid #F1F5F9',
                fontSize: 13,
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{link.entity_type}</span>
                  <span style={{ color: '#64748B', marginLeft: 8, fontFamily: 'monospace' }}>{String(link.entity_id).slice(0, 8)}...</span>
                </span>
                <button
                  onClick={() => copyToClipboard(link.entity_id, 'Entity ID')}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderRadius: 4,
                    fontSize: 10,
                    cursor: 'pointer'
                  }}
                >
                  Copy ID
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #F1F5F9',
        display: 'flex',
        gap: 10,
        backgroundColor: 'white'
      }}>
        <button
          onClick={() => copyToClipboard(media.urls?.original, 'Original URL')}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
        >
          Copy URL
        </button>
        
        <label
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            cursor: replacing ? 'not-allowed' : 'pointer',
            textAlign: 'center',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            opacity: replacing ? 0.6 : 1
          }}
        >
          {replacing ? 'Replacing...' : 'Replace'}
          <input type="file" accept="image/*,video/*" onChange={handleReplace} style={{ display: 'none' }} disabled={replacing} />
        </label>
        
        <button
          onClick={handleDelete}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#DC2626',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#B91C1C'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default MediaDrawer;